/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { LayoutSplashScreen } from '../../_metronic/layout/core';
import { getDefaultCustomer } from '../../_metronic/helpers/UserRoleHelpers';
import { AUTH0_AUDIENCE } from '../auth0/config';

const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL
})

const TOKEN_SCOPE = "read:current_user app_metadata";

// O claim de autorização é fotografado no login e vale até o token expirar (24h no
// padrão do SPA). Quando uma loja entra no grupo, quem já está logado não a enxerga
// até lá. Uma renovação forçada no bootstrap e uma autocura no primeiro 403 cobrem a
// janela — ambas UMA vez por sessão, para não virar laço.
const BOOTSTRAP_FLAG = 'konfidency:claimRefreshed';
const HEAL_FLAG = 'konfidency:claimHealed';

const markOnce = (flag: string): boolean => {
    try {
        if (window.sessionStorage.getItem(flag)) return false;
        window.sessionStorage.setItem(flag, '1');
        return true;
    } catch {
        // Sem sessionStorage (aba privada, storage bloqueado): não renova, que é o
        // comportamento de antes — nunca repete em laço.
        return false;
    }
};

type Props = {
    children: any
  }


const AxiosInterceptor: React.FC<Props> = ({ children }) => {

    const { getAccessTokenSilently, user, isLoading, isAuthenticated } = useAuth0();
    const [justLoaded, setJustLoaded] = useState(true);

    useEffect(() => {

        var interceptor = axios.interceptors.request.use(
            async (config: any) => {

                // Skip token fetch if not authenticated
                if (!isAuthenticated) {
                    return config;
                }

                const token = await getAccessTokenSilently({
                    audience: AUTH0_AUDIENCE,
                    scope: TOKEN_SCOPE,
                });

                if (user) {
                    const storageCustomer = window.localStorage.getItem("activeCustomer");
                    const customer = storageCustomer || getDefaultCustomer(user);

                    config.headers.Authorization = `Bearer ${token}`;
                    config.baseURL = `${process.env.REACT_APP_API_URL}/${customer}`;
                }

                return config;
            });

        // Autocura: 403 pode ser claim velho — loja concedida depois que este token foi
        // emitido. Renova ignorando o cache e repete a requisição UMA vez. Se o 403 for
        // real, a repetição falha igual e o erro segue para a tela.
        var responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error: any) => {
                const config = error?.config;

                if (error?.response?.status !== 403 || !config || config.__claimRetried) {
                    return Promise.reject(error);
                }
                if (!isAuthenticated || !markOnce(HEAL_FLAG)) {
                    return Promise.reject(error);
                }

                try {
                    await getAccessTokenSilently({
                        audience: AUTH0_AUDIENCE,
                        scope: TOKEN_SCOPE,
                        ignoreCache: true,
                    });
                } catch {
                    return Promise.reject(error);
                }

                // O interceptor de requisição relê o token, agora atualizado no cache.
                config.__claimRetried = true;
                return axios.request(config);
            });

        // Renovação no bootstrap, uma vez por sessão: pega concessões feitas enquanto
        // a pessoa estava com a aba fechada, sem esperar o token expirar.
        if (isAuthenticated && markOnce(BOOTSTRAP_FLAG)) {
            getAccessTokenSilently({ audience: AUTH0_AUDIENCE, scope: TOKEN_SCOPE, ignoreCache: true })
                .catch(() => {
                    // Falhar aqui não impede o uso: o token em cache continua valendo.
                });
        }

        // workaround: wait 200ms before rendering content
        if (!isLoading && justLoaded) {
            setTimeout(() => { setJustLoaded(false) }, 200);
        }

        // Antes isto removia de `instance.interceptors.response` — objeto e array
        // errados —, então nada era removido e os interceptors empilhavam a cada
        // re-render.
        return () => {
            axios.interceptors.request.eject(interceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };

    }, [isLoading, isAuthenticated]);


    return (!isLoading && !justLoaded) ? children:  <LayoutSplashScreen />;
}


export default instance;
export { AxiosInterceptor }
